/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { FC, useState } from "react";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import { userAccountService } from "../services";
import { ICreateCDApplicationRequestBody } from "../services/user-account-service.types";
import { useUser } from "./UserContext";
import { IOnlineApplication } from "../services/online-application.types";
import { ICompileTimeError } from "../config/common";
import {
  applicationCDFromDataInitialValues,
  FormProps,
  ICDApplicationPage,
  ICDFormValues,
} from "../pages/JoiningPages/CastingDirector/constants";
import { CD_TYPE_CODE } from "../constants/PerformerType";
import { mapCDFormData } from "../pages/JoiningPages/CastingDirector/utils";
import { useApplication } from "./ApplicationContext";

export type ResumeApplication = (
  securityKey: string,
  onlineApplicationId: string,
  step?: string
) => Promise<{ success: boolean; performerType?: number | undefined }>;

interface ICastingApplicationContextParams {
  createCDApplication: (
    body: ICreateCDApplicationRequestBody
  ) => Promise<boolean | ICompileTimeError>;
  applicationCDData: ICDFormValues;
  saveCDApplicationData: (step: ICDApplicationPage, value: FormProps) => void;
  resumeApplicationCD: ResumeApplication;
}

export const CastingApplicationContextProvider =
  React.createContext<ICastingApplicationContextParams>({
    applicationCDData: applicationCDFromDataInitialValues,
    saveCDApplicationData: () => {},
    createCDApplication: () => new Promise(() => {}),
    resumeApplicationCD: () => new Promise(() => {}),
  });

export const CastingContextProvider: FC = ({ children }) => {
  const {
    setOnlineApplicationId,
    setSecurityKey,
    setStepNumber,
    setApplicationDetails,
  } = useApplication();

  const isNewJoiningCDProcess = useLaunchDarklyFlags(
    FEATURE_FLAGS.JoiningCDProcess
  );
  const [applicationCDData, setApplicationCDData] = useState<ICDFormValues>(
    applicationCDFromDataInitialValues
  );
  const { updateUser } = useUser();

  const saveCDApplicationData = (
    step: ICDApplicationPage,
    value: FormProps
  ) => {
    setApplicationCDData((data) => ({
      ...data,
      [step]: value,
    }));
  };

  const createCDApplication = async (
    body: ICreateCDApplicationRequestBody
  ): Promise<boolean | ICompileTimeError> => {
    try {
      const result = await userAccountService.createCDApplication(
        body,
        isNewJoiningCDProcess
      );

      const cdApplicationData = isNewJoiningCDProcess
        ? { ...result, id: result.applicationId }
        : result;

      setApplicationDetails({
        ...cdApplicationData,
        isOrganisationMember: !!body.organisationLookUpId,
        isCDApplication: true,
      } as IOnlineApplication);

      return true;
    } catch (error: unknown) {
      return (error as ICompileTimeError) || false;
    }
  };

  const resumeApplicationCD = async (
    securityKey: string,
    onlineApplicationId: string
  ) => {
    try {
      const applicationCD = await userAccountService.getCDApplication({
        customerid: onlineApplicationId,
        securityKey,
      });

      const formData = mapCDFormData(applicationCD);

      setApplicationCDData(formData);
      setOnlineApplicationId(Number(applicationCD.id));
      setSecurityKey(applicationCD.securityKey);
      updateUser((user) => ({ ...user, isCDApplication: true }));

      setStepNumber(1);

      return { success: true, performerType: CD_TYPE_CODE };
    } catch (error: any) {
      console.error(error);

      return { success: false };
    }
  };

  return (
    <CastingApplicationContextProvider.Provider
      value={{
        applicationCDData,
        createCDApplication,
        saveCDApplicationData,
        resumeApplicationCD,
      }}
    >
      {children}
    </CastingApplicationContextProvider.Provider>
  );
};

export const useCastingApplication = () =>
  React.useContext(CastingApplicationContextProvider);
